import { useCallback, useEffect, useRef, useState } from "react";

export enum ScrollDirection {
    Up = "up",
    Down = "down",
    Still = "still"
}

type useDetectScrollProps = {
    threshold?: number;
};

const useDetectScroll = ({ threshold = 0 }: useDetectScrollProps = {}) => {
    const [direction, setDirection] = useState(ScrollDirection.Still);

    const thr = Math.max(0, threshold);
    const lastScroll = useRef(0);
    const ticking = useRef(false);
    const scrollTimer = useRef<NodeJS.Timeout | undefined>(undefined);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const updateScrollDir = useCallback(() => {
        const scroll = window.scrollY;

        if (Math.abs(scroll - lastScroll.current) >= threshold) {
            setDirection(
                scroll > lastScroll.current
                    ? ScrollDirection.Down
                    : ScrollDirection.Up
            );
            lastScroll.current = Math.max(0, scroll);
        }

        ticking.current = false;
    }, [thr]);

    useEffect(() => {
        lastScroll.current = window.scrollY;

        const onScroll = () => {
            if (!ticking.current) {
                window.requestAnimationFrame(updateScrollDir);
                clearTimeout(scrollTimer.current);
                scrollTimer.current = setTimeout(
                    () => setDirection(ScrollDirection.Still),
                    100
                );
                ticking.current = true;
            }
        };

        window.addEventListener("scroll", onScroll);

        return () => window.removeEventListener("scroll", onScroll);
    }, [updateScrollDir]);

    return { direction };
};

export { useDetectScroll };
